<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header style InteractiveMap.vue -->
    <div class="bg-white border-b border-gray-200 px-6 py-4">
      <div class="flex items-center">
        <button
          class="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 mr-4"
          @click="goBackToGestionMemos">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 class="text-2xl font-bold text-gray-900 flex-1 truncate">Collection</h1>
      </div>
      <Breadcrumbs class="mt-2 ml-12" />
    </div>
    <!-- Contenu principal -->
    <div class="anamnesis-container">
      <Breadcrumbs v-if="false" /> <!-- Breadcrumbs déplacé dans le header -->
      <div v-if="isLoading" class="text-center py-10">
        <p>Chargement de la collection...</p>
      </div>
      <div v-else-if="error" class="text-center py-10 text-red-500">
        <p>Erreur: {{ error }}</p>
      </div>
      <div v-else-if="collection">
        <MemosSubset 
          :memos="memosInCollection"
          :collection="collection"
          :all-accessible-memos="allAccessibleMemos"
          @refresh-data="refreshData"
          @collection-memo-added="handleMemoAdded"
          @collection-memo-removed="handleMemoRemoved"
        />
      </div>
      <Notification ref="notification" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAssetsCacheStore } from '@/stores/assetsCache';
import { useBreadcrumbsStore } from '@/stores/breadcrumbs';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import * as collectionsApi from '@/api/collections';
import * as memosApi from '@/api/memos';
import type { MemoCollection } from '@/api/types/memos/collections';
import type { Memo } from '@/api/types/memos/types';

import Breadcrumbs from '@/components/Breadcrumbs.vue';
import Notification from '@/components/Notification.vue';
import MemosSubset from '@/components/subsets/MemosSubset.vue';

const route = useRoute();
const router = useRouter();
const breadcrumbsStore = useBreadcrumbsStore();
const assetsCacheStore = useAssetsCacheStore();

// État local
const collection = ref<MemoCollection | null>(null);
const memosInCollection = ref<Memo[]>([]);
const allAccessibleMemos = ref<Memo[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);
const notification = ref<InstanceType<typeof Notification> | null>(null);

const collectionId = computed(() => route.params.id as string);

const loadCollectionData = async (id: string): Promise<void> => {
  if (!id) return;
  
  isLoading.value = true;
  error.value = null;
  
  try {
    // Récupérer la collection depuis le cache
    const cachedCollection = await assetsCacheStore.getAssetByUuid<MemoCollection>('collections', id);
    
    if (cachedCollection) {
      collection.value = cachedCollection;
    } else {
      // Si pas dans le cache, faire un appel API direct (cas rare)
      const response = await collectionsApi.collections.getCollection(id);
      if (response.success && response.data) {
        collection.value = response.data;
        // Ajouter au cache
        assetsCacheStore.addAssetToCache('collections', response.data);
      }
    }
    
    // Récupérer les mémos de cette collection
    if (collection.value) {
      const memosResponse = await collectionsApi.collections.getMemosInCollection(id);
      if (memosResponse.success && memosResponse.data) {
        memosInCollection.value = memosResponse.data;
      }
    }
    
    // Récupérer tous les mémos accessibles pour le modal
    try {
      allAccessibleMemos.value = await memosApi.memos.getAccessible();
    } catch (e) {
      console.warn('Erreur lors du chargement des mémos accessibles:', e);
      // Ne pas bloquer le chargement principal si cette requête échoue
    }
  } catch (e: any) {
    error.value = e.message || 'Erreur lors du chargement';
    console.error('Erreur lors du chargement de la collection:', e);
  } finally {
    isLoading.value = false;
  }
};

const refreshData = async (): Promise<void> => {
  await loadCollectionData(collectionId.value);
};

// Gestionnaires d'événements pour les opérations sur les mémos
const handleMemoAdded = async (data: { 
  memoCount: number; 
  collectionName: string;
  alreadyInCollection?: number;
  errors?: number;
}): Promise<void> => {
  let message = '';
  let type: 'success' | 'warning' | 'error' = 'success';
  
  if (data.memoCount > 0) {
    message = `${data.memoCount} mémo(s) ajouté(s) à la collection "${data.collectionName}"`;
    
    if (data.alreadyInCollection && data.alreadyInCollection > 0) {
      message += `. ${data.alreadyInCollection} mémo(s) déjà présent(s)`;
    }
    
    if (data.errors && data.errors > 0) {
      message += `. ${data.errors} erreur(s)`;
      type = 'warning';
    }
  } else if (data.alreadyInCollection && data.alreadyInCollection > 0) {
    message = `${data.alreadyInCollection} mémo(s) déjà présent(s) dans la collection "${data.collectionName}"`;
    type = 'warning';
  } else if (data.errors && data.errors > 0) {
    message = `Erreur lors de l'ajout des mémos à la collection "${data.collectionName}"`;
    type = 'error';
  }
  
  notification.value?.showNotification(message, type);
  
  // Rafraîchir la liste des mémos seulement si au moins un mémo a été ajouté
  if (data.memoCount > 0) {
    await refreshData();
  }
};

const handleMemoRemoved = async (data: { memoCount: number; collectionName: string }): Promise<void> => {
  notification.value?.showNotification(
    `${data.memoCount} mémo(s) retiré(s) de la collection "${data.collectionName}"`,
    'success'
  );
  // Rafraîchir la liste des mémos
  await refreshData();
};

onMounted(() => {
  if (collectionId.value) {
    loadCollectionData(collectionId.value);
  }
});

watch(collectionId, (newId) => {
  if (newId) {
    loadCollectionData(newId);
  }
});

watch(collection, (newCollection) => {
  if (newCollection) {
    breadcrumbsStore.updateBreadcrumbs([
      { name: 'Mes Collections', path: '/collections' },
      { name: newCollection.name, path: `/collections/${newCollection.uuid}` }
    ]);
  }
});

// Mettre à jour les breadcrumbs quand le chargement est terminé et qu'on n'a pas de collection
watch([isLoading, collection], ([loading, coll]) => {
  if (!loading && !coll) {
    breadcrumbsStore.updateBreadcrumbs([
      { name: 'Mes Collections', path: '/collections' },
      { name: 'Collection non trouvée', path: '' }
    ]);
  }
});

// Fonction pour retourner à la gestion des mémos
const goBackToGestionMemos = (): void => {
  router.push('/gestion-memos');
};
</script> 